import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Client, ClientStatus } from '../../context/ClientContext';
import { Badge, Button, Dropdown, Icon, Input, SearchableSelect, SidePanel } from '../ui';

interface EditClientSidepanelProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onSaved?: () => void;
}

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Prospect', label: 'Prospect' },
  { value: 'Paused', label: 'Paused' },
  { value: 'Archived', label: 'Archived' },
];

interface BoardOption {
  id: string;
  board_name: string;
  client_id: string | null;
}

/**
 * Edit Client slide-over — mirror of the Add Client panel for existing
 * clients, plus the Trello board assignment that links a client to their
 * Client Comms board (writes comms_boards.client_id).
 */
export default function EditClientSidepanel({ isOpen, onClose, client, onSaved }: EditClientSidepanelProps) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    industry: '',
    status: 'Active' as ClientStatus,
    tags: [] as string[],
  });
  const [newTag, setNewTag] = useState('');
  const [boards, setBoards] = useState<BoardOption[]>([]);
  const [boardId, setBoardId] = useState<string | null>(null);
  const [initialBoardId, setInitialBoardId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form and load boards each time the panel opens
  useEffect(() => {
    if (!isOpen || !client) return;

    setForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      industry: client.industry || '',
      status: client.status,
      tags: client.tags || [],
    });
    setNewTag('');
    setError(null);

    const loadBoards = async () => {
      const { data, error: boardsError } = await supabase
        .from('comms_boards')
        .select('id, board_name, client_id')
        .eq('is_active', true)
        .order('board_name', { ascending: true });
      if (boardsError) {
        console.error('Error loading Trello boards:', boardsError);
        return;
      }
      const list = (data as BoardOption[]) || [];
      setBoards(list);
      const assigned = list.find((b) => b.client_id === client.id)?.id ?? null;
      setBoardId(assigned);
      setInitialBoardId(assigned);
    };
    loadBoards();
  }, [isOpen, client]);

  const addTag = () => {
    const tag = newTag.trim();
    if (tag && !form.tags.includes(tag)) {
      setForm({ ...form, tags: [...form.tags, tag] });
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });
  };

  const handleSave = async () => {
    if (!client) return;
    if (!form.name.trim()) {
      setError('Client name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { error: updateError } = await supabase
        .from('clients')
        .update({
          name: form.name,
          email: form.email || null,
          phone: form.phone || null,
          industry: form.industry || null,
          status: form.status,
          tags: form.tags,
        })
        .eq('id', client.id);
      if (updateError) throw updateError;

      if (boardId !== initialBoardId) {
        // Unassign any board currently pointing at this client...
        const { error: unassignError } = await supabase
          .from('comms_boards')
          .update({ client_id: null })
          .eq('client_id', client.id);
        if (unassignError) throw unassignError;

        // ...then point the chosen board here (steals it if another client had it)
        if (boardId) {
          const { error: assignError } = await supabase
            .from('comms_boards')
            .update({ client_id: client.id })
            .eq('id', boardId);
          if (assignError) throw assignError;
        }
      }

      onClose();
      onSaved?.();
    } catch (err) {
      console.error('Error saving client:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  const boardOptions = boards.map((b) => ({
    value: b.id,
    label: b.board_name,
    hint: b.client_id && b.client_id !== client?.id ? 'in use' : undefined,
  }));

  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      eyebrow={client?.name || 'Client'}
      title="Edit Client"
      footer={
        <>
          <Button
            icon="check"
            onClick={handleSave}
            disabled={isSubmitting || !form.name.trim()}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error ? (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400, #E5484D)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        ) : null}

        <Input
          label="Client Name"
          icon="building-2"
          placeholder="Acme Industries..."
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onKeyDown={submitOnEnter}
          required
        />

        <Input
          label="Email"
          icon="mail"
          type="email"
          placeholder="name@company.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          onKeyDown={submitOnEnter}
        />

        <Input
          label="Phone"
          type="tel"
          placeholder="Phone number..."
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          onKeyDown={submitOnEnter}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <Input
            label="Industry"
            placeholder="Industry..."
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
            onKeyDown={submitOnEnter}
          />
          <Dropdown
            label="Status"
            options={STATUS_OPTIONS}
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value as ClientStatus })}
          />
        </div>

        <div>
          <SearchableSelect
            label="Trello Board"
            placeholder="Assign a board..."
            searchPlaceholder="Search boards..."
            clearLabel="No board"
            options={boardOptions}
            value={boardId}
            onChange={setBoardId}
            allowClear
          />
          <p style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            Links this client to their Client Comms board. Picking a board marked
            "in use" moves it over from its current client.
          </p>
        </div>

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Tags</div>
          {form.tags.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {form.tags.map((tag) => (
                <Badge key={tag} tone="neutral">
                  {tag}
                  <button
                    type="button"
                    className="pressable"
                    aria-label={`Remove ${tag}`}
                    onClick={() => removeTag(tag)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: 0,
                      marginLeft: '2px',
                      border: 'none',
                      background: 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon name="x" size={10} />
                  </button>
                </Badge>
              ))}
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <Input
                placeholder="Add a tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
            </div>
            <Button type="button" variant="secondary" onClick={addTag}>Add</Button>
          </div>
        </div>
      </div>
    </SidePanel>
  );
}
